import { DateRangeObject } from 'shared/types/analytics';
import { DayMomentValidation } from 'shared/types/time';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { FieldTypes } from 'shared/domain/issueForm/types/fieldTypes';
import { ChartValuesAndLabels } from 'shared/types/analytics';
import { IssueEntity } from 'serviceWorker/repository/issue/entity';
import {
  HashMap,
  LabelledEntity,
  UntranslatedLabelledEntity,
} from 'shared/types/commonView';
import { isoToProjectTimezonedDate } from 'shared/utils/date/dates';
import { InspectionEntity } from 'serviceWorker/repository/inspection/entity';

const noneId = 'issue_field_placeholder_empty';
type Issue = IssueEntity;

export function increaseEntitySeries(
  seriesObject: HashMap<number>,
  labelsObject: HashMap<LabelledEntity | UntranslatedLabelledEntity>,
  value: LabelledEntity
): void {
  const { _id, label } = value;

  if (seriesObject[_id]) {
    seriesObject[_id] += 1;
  } else {
    seriesObject[_id] = 1;
    labelsObject[_id] = {
      _id,
      label,
    };
  }
}

export function increaseNoneSeries(
  seriesObject: HashMap<number>,
  labelsObject: HashMap<UntranslatedLabelledEntity | LabelledEntity>
): void {
  if (seriesObject[noneId]) {
    seriesObject[noneId] += 1;
  } else {
    seriesObject[noneId] = 1;
    labelsObject[noneId] = {
      _id: noneId,
      labelId: noneId,
    };
  }
}

function canCheckLength(value: unknown): value is { length: number } {
  return Array.isArray(value) || typeof value === 'string';
}

function isLabelledEntity(value: unknown): value is LabelledEntity {
  // @ts-ignore duck typing
  return typeof value === 'object' && value && value._id && value.label;
}

export function filterByDateRange(
  dateRange: DateRangeObject,
  issueDetectionDate: string,
  timezone: string
): boolean {
  const detectionDateTime = isoToProjectTimezonedDate(
    issueDetectionDate,
    timezone
  ).valueOf();

  const fromTimeTimezoned = isoToProjectTimezonedDate(
    dateRange.from,
    timezone
  ).valueOf();
  const toTimeTimezoned = isoToProjectTimezonedDate(
    dateRange.to,
    timezone,
    DayMomentValidation.end
  ).valueOf();

  return (
    detectionDateTime >= fromTimeTimezoned &&
    detectionDateTime <= toTimeTimezoned
  );
}

function filterByMultiselect(
  field: string[],
  filterValue: string[],
  entity: HashMap<any>
): boolean {
  if (filterValue.length === 0) {
    return true;
  }

  const entityFieldValue: unknown = field.reduce(
    (fieldValue, currentFieldName) => {
      return fieldValue[currentFieldName];
    },
    entity
  );

  if (canCheckLength(entityFieldValue) && entityFieldValue.length === 0) {
    return false;
  }

  if (Array.isArray(entityFieldValue) && entityFieldValue.length !== 0) {
    return filterValue.some((item1) =>
      entityFieldValue.some((item2) => item1 === item2._id)
    );
  }

  if (
    typeof entityFieldValue === 'string' &&
    entityFieldValue.length !== 0
  ) {
    return filterValue.includes(entityFieldValue);
  }

  if (isLabelledEntity(entityFieldValue)) {
    return filterValue.some((value) => value === entityFieldValue._id);
  }

  return false;
}

export type MultiselectFilter<T = HashMap<any>> = (
  filterValue: string[],
  entity: T
) => boolean;

export const filterBySubcontractor: MultiselectFilter<Issue> = (
  filterValue,
  issue
) =>
  filterByMultiselect(
    [FieldTypes.primaryData, IssueFieldNames.subcontractors],
    filterValue,
    issue
  );
export const filterByProcess: MultiselectFilter = (filterValue, entity) =>
  filterByMultiselect(['process'], filterValue, entity);

export const filterBySite: MultiselectFilter<Issue> = (
  filterValue,
  issue
) =>
  filterByMultiselect(
    [FieldTypes.primaryData, IssueFieldNames.site],
    filterValue,
    issue
  );

export const filterByWorktype: MultiselectFilter<Issue> = (
  filterValue,
  issue
) =>
  filterByMultiselect(
    [FieldTypes.extendedData, IssueFieldNames.workTypes],
    filterValue,
    issue
  );

export const filterByAssignee: MultiselectFilter<Issue> = (
  filterValue,
  issue
) =>
  filterByMultiselect(
    [FieldTypes.primaryData, IssueFieldNames.assignee],
    filterValue,
    issue
  );

export const filterByImpact: MultiselectFilter<Issue> = (
  filterValue,
  issue
) =>
  filterByMultiselect(
    [FieldTypes.extendedData, IssueFieldNames.impact],
    filterValue,
    issue
  );

export const filterInspectionBySite: MultiselectFilter<
  InspectionEntity
> = (filterValue, inspection) =>
  filterByMultiselect(['site'], filterValue, inspection);

export const filterByCreatedBy: MultiselectFilter = (
  filterValue,
  entity
) => filterByMultiselect(['createdBy'], filterValue, entity);

export function toSortedValuesAndLabels(
  series: HashMap<number>,
  labels: HashMap<LabelledEntity | UntranslatedLabelledEntity>
): ChartValuesAndLabels {
  const sortedSeries = Object.entries(series).sort(
    (entryA: [string, number], entryB: [string, number]) => {
      return entryB[1] - entryA[1];
    }
  );

  return sortedSeries.reduce(
    (result: [number[], string[]], sortedEntry: [string, number]) => {
      result[0].push(sortedEntry[1]);
      result[1].push(
        // @ts-ignore label is of type string | { labelId } so we know when to translate. Don't know how to properly type it yet.
        labels[sortedEntry[0]].label || {
          // @ts-ignore label is of type string | { labelId } so we know when to translate. Don't know how to properly type it yet.
          labelId: labels[sortedEntry[0]].labelId,
        }
      );
      return result;
    },
    [[], []]
  );
}
